import { clamp } from "@prismicio/editor-support/Number"
import { isDefined } from "@prismicio/editor-support/Type"
import { useState } from "react"

import type { SX } from "../../theme"
import { Badge } from "../Badge"
import { BaseInput } from "../BaseInput"
import type { BaseInputProps } from "../BaseInput/BaseInput"

export interface NumberInputProps {
  autoComplete?: string
  id?: string
  max?: number
  min?: number
  name?: string
  placeholder?: string
  padding: BaseInputProps["padding"]
  readOnly?: boolean
  value?: number
  sx?: SX
  onValueChange?: (value: number | undefined) => void
}

export function NumberInput(props: NumberInputProps) {
  const { max, min, onValueChange, value, ...otherProps } = props

  /* A draft value is only defined when the user is typing something new.
  It allows us to represent temporarily malformed "numbers". */
  const [draftValue, setDraftValue] = useState<string>()

  const displayedValue = String(draftValue ?? value ?? "")

  function handleChange(value: string) {
    if (!isValidDraftValue(value)) return
    setDraftValue(value)
  }

  function handleBlur() {
    // Nothing to do if the user didn't write anything.
    if (draftValue === undefined) return

    // Always reset the draft value when the user is done so that the Input becomes controlled by our parent again.
    setDraftValue(undefined)

    // The user explicitly emptied the field.
    if (draftValue === "") return onValueChange?.(undefined)

    const draftValueNumber = Number(draftValue.replace(",", "."))

    // The user did not enter a full, valid number, for instance, only a minus sign: abort.
    if (Number.isNaN(draftValueNumber)) return

    const newValue = clamp(draftValueNumber, { min, max })

    if (newValue !== value) onValueChange?.(newValue)
  }

  return (
    <BaseInput
      {...otherProps}
      endAdornment={<MinMaxBadge min={min} max={max} />}
      inputMode="decimal"
      onBlur={handleBlur}
      onValueChange={handleChange}
      value={displayedValue}
    />
  )
}

interface MinMaxBadgeProps {
  min?: number
  max?: number
}

function MinMaxBadge(props: MinMaxBadgeProps) {
  const { min, max } = props

  if (min === undefined && max === undefined) return null

  const badgeText = [
    min !== undefined ? `Min: ${min}` : "",
    max !== undefined ? `Max: ${max}` : ""
  ]
    .filter(isDefined)
    .join(" ")

  return <Badge title={badgeText} maxWidth={150} />
}

const validDraftValueRegex = /^[-+]?[0-9]*[.,]?[0-9]*$/
export function isValidDraftValue(value: string): boolean {
  return validDraftValueRegex.test(value)
}
